//aiModels.js
// Base URL for your API endpoints
const BASE_URL = "https://api.health.easiofy.com";


/**
 * This object describes the AI Models your system supports.
 * Each model has:
 *  - id, displayName
 *  - baseEndpoint, endpoints
 *  - costKey: reference to a cost variable (e.g., "XRay-Fracture")
 *  - supportedModalities: an array of which imaging modalities are valid
 *  - steps: the sequence of steps to run
 *  - requiresPolling: whether we must poll the server after each step
 *  - processParams(...) / buildRequestBody(...) - how to build the request body for each step
 */
export const AI_MODELS = {
  XRAY_FRACTURE: {
    id: "XRAY_FRACTURE",
    displayName: "XRay-Fracture",
    baseEndpoint: `${BASE_URL}/BoneFracture`,
    costKey: "XRay-Fracture", // reference to your local cost variable
    supportedModalities: ["xray", "x-ray", "xr", "x ray"],
    endpoints: {
      health: "/health", // GET:  e.g. https://api.health.easiofy.com/BoneFracture/health
      process: "/get_path", // POST: e.g. https://api.health.easiofy.com/BoneFracture/get_path
      status: "/status/:taskId", // GET:  e.g. https://api.health.easiofy.com/BoneFracture/status/<taskId>
      download: "/download/:taskId", // Add download endpoint
    },
    steps: ["process", "download"], // Add download step
    requiresPolling: true,

    /**
     * This function sets up the base parameters from your patient data
     * (like folder paths, admin name, patient ID, etc.)
     */
    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    /**
     * Used by the AIModelHandler to build request bodies for each step.
     */
    buildRequestBody: (step, params) => {
      // For bone fracture, we do it in one step => "process"
      if (step === "process") {
        return {
          path: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
          adminName1: params.adminName ? params.adminName.trim() : "",
          patientId: params.patientId ? params.patientId.trim() : "",
        };
      }
      return {};
    },
  },

  CONTOUR_ABDOMEN: {
    id: "CONTOUR_ABDOMEN",
    displayName: "Contour-Abdomen",
    costKey: "Contour-Abdomen",
    supportedModalities: ["ct"],
    baseEndpoint: `${BASE_URL}/contouring__abdomen`,
    endpoints: {
      health: "/health",
      dcmtonrrd: "/dcmtonrrd",
      abdomen_heart_segmentation: "/abdomen_heart_segmentation",
      merging_nrrd: "/merging_nrrd",
      convert: "/convert",
      status: "/status/:taskId",
    },
    steps: ["dcmtonrrd", "abdomen_heart_segmentation", "merging_nrrd", "convert"],
    requiresPolling: false,
    useUrlEncoded: true,

    // Base parameters
    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
      model_name: "Abdomen_heart",
    }),

    // Each step can produce data for subsequent steps (e.g., nrrd_path).
    buildRequestBody: (step, currentParams) => {
      // Generate unique task_id with 4-digit random number
      const randomNum = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
      const task_id = currentParams.task_id || `${currentParams.adminName}_${currentParams.patientId}_${randomNum}`;

      // Store task_id for reuse in subsequent steps
      if (!currentParams.task_id) {
        currentParams.task_id = task_id;
      }

      switch (step) {
        case "dcmtonrrd":
          // For dcmtonrrd, we'll send a ZIP file (handled in AIModelHandler)
          // Here we just return the parameters needed
          return {
            model_name: currentParams.model_name,
            task_id,
            // Note: dicom_zip will be added as FormData in AIModelHandler
          };
        case "abdomen_heart_segmentation":
          return {
            nrrd_path: currentParams.nrrd_path,
            model_name: currentParams.model_name,
            dicom_dir: currentParams.dicom_files_dir || currentParams.local_dicom_dir,
            task_id,
          };
        case "merging_nrrd":
          return {
            heart_nrrd: currentParams.heart_nrrd || currentParams.processed_nrrd,
            abdomen_nrrd: currentParams.abdomen_nrrd || currentParams.processed_nrrd,
            dicom_dir: currentParams.dicom_files_dir || currentParams.local_dicom_dir || currentParams.dicom_dir,
            model_name: currentParams.model_name,
            task_id,
          };
        case "convert":
          return {
            dicom_dir: currentParams.dicom_files_dir || currentParams.updated_dicom_dir || currentParams.dicom_dir || currentParams.local_dicom_dir,
            // The merging_nrrd endpoint already returns a 'seg_nrrd' property, so use that directly if available
            seg_nrrd: currentParams.seg_nrrd || currentParams.processed_nrrd || currentParams.merged_nrrd,
            model_name: currentParams.model_name,
            task_id,
          };
        default:
          return {};
      }
    }
  },



  TUMOR_ABDOMEN: {
    id: "TUMOR_ABDOMEN",
    displayName: "Tumor-Abdomen",
    costKey: "Tumor-Abdomen",
    supportedModalities: ["ct"],
    baseEndpoint: `${BASE_URL}/Abdomen_tumor_analysis`,
    endpoints: {
      health: "/health",
      download: "/download-dicom",
      convert: "/convert-dicom-to-nifti",
      process: "/run-inference",
      segmentation: "/dicomseg",
    },
    steps: ["download", "convert", "process", "segmentation"],
    requiresPolling: false,
    useUrlEncoded: true, // Keep as true for most endpoints, handle download separately

    // Base parameters
    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
      model_name: "Abdomen Tumor",
    }),

    // Each step can produce data for subsequent steps
    buildRequestBody: (step, currentParams) => {
      // Generate unique patient ID with 4-digit random suffix
      const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
      const uniquePatientId = currentParams.uniquePatientId || `${currentParams.patientId}${randomSuffix}`;

      // Store unique patient ID for reuse in subsequent steps
      if (!currentParams.uniquePatientId) {
        currentParams.uniquePatientId = uniquePatientId;
      }

      switch (step) {
        case "download":
          return {
            patient_id: uniquePatientId,
            // Note: zip_file will be added as FormData in AIModelHandler
          };
        case "convert":
          // Store firebase_path in currentParams for later use, but don't send it to the API
          if (
            !currentParams._storedFirebasePath &&
            currentParams.decryptedFolderPath
          ) {
            currentParams._storedFirebasePath = currentParams.decryptedFolderPath;
          }
          return {
            dicom_path: currentParams.dicom_path,
            patient_id: currentParams.uniquePatientId || currentParams.patientId,
          };
        case "process":
          return {
            nifti_file: currentParams.nifti_file,
            dicom_path: currentParams.dicom_path,
            patient_id: currentParams.patient_id || currentParams.patientId,
          };
        case "segmentation":
          // Make sure we have all required parameters
          if (!currentParams.dicom_path) {
            console.error("Missing dicom_path in parameters");
          }
          if (
            !currentParams.seg_nifti &&
            currentParams.seg_niftis &&
            currentParams.seg_niftis.length > 0
          ) {
            // If seg_nifti is missing but we have seg_niftis array, use the first one
            currentParams.seg_nifti = currentParams.seg_niftis[0];
          }
          // Check if we have all required parameters
          return {
            dicom_path: currentParams.dicom_path,
            seg_nifti: currentParams.seg_nifti,
            patient_id: currentParams.uniquePatientId || currentParams.patientId,
          };
        default:
          return {};
      }
    },
  },

  THREED_ABDOMEN: {
    id: "THREED_ABDOMEN",
    displayName: "3D-Abdomen",
    costKey: "3D-Abdomen",
    supportedModalities: ["ct"],
    baseEndpoint: `${BASE_URL}/3d`,
    endpoints: {
      health: "/health",
      glb: "/glb",
    },
    steps: ["glb"],
    requiresPolling: false,
    useUrlEncoded: true,

    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    buildRequestBody: (step, params) => {
      if (step === "glb") {
        // Generate a unique process ID for this request
        const processId = `${params.adminName}_${params.patientId}_${Date.now()}`;

        return {
          firebase_dir: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
          model_name: "Abdomen", // Based on api.py, this should be "Abdomen" for abdomen models
          process_id: processId,
        };
      }
      return {};
    },
  },

  "Contour-HaN": {
    id: "Contour-HaN",
    displayName: "Contour-HaN",
    costKey: "Contour-HaN",
    supportedModalities: ["ct"],
    baseEndpoint: `${BASE_URL}/headandneckv2`,
    endpoints: {
      dicomToNifti: "/dicom-to-nifti",
      convertToDicomSeg: "/convert-to-dicomseg",
      health: "/health",
      segment: "/segment",
    },
    steps: ["health", "dicomToNifti", "segment", "convertToDicomSeg"],
    requiresPolling: false,
    useUrlEncoded: false, // Changed to false to support multipart/form-data for ZIP upload

    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    buildRequestBody: (step, params) => {
      // Generate unique patient_id with 4-digit random suffix
      const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
      const uniquePatientId = params.uniquePatientId || `${params.patientId}${randomSuffix}`;

      // Store unique patient_id for reuse in subsequent steps
      if (!params.uniquePatientId) {
        params.uniquePatientId = uniquePatientId;
      }

      if (step === "dicomToNifti") {
        return {
          patient_id: uniquePatientId,
          // Note: dicom_zip will be added as FormData in AIModelHandler
        };
      } else if (step === "segment") {
        return {
          nifti_path: params.niftiFile || "",
          patient_id: params.uniquePatientId || uniquePatientId,
        };
      } else if (step === "convertToDicomSeg") {
        return {
          dicom_firebase_path: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
          seg_nrrd: params.segNrrd || "", // to be set after segment
          model_name: "Contour-HaN",
          patient_id: params.uniquePatientId || uniquePatientId,
        };
      }
      return {};
    },
  },

  CXR_TB: {
    id: "CXR_TB",
    displayName: "CXR-TB",
    baseEndpoint: `${BASE_URL}/TB`,
    costKey: "CXR-TB", // Updated cost key for new database structure
    supportedModalities: ["xray", "x-ray", "xr", "x ray"],
    endpoints: {
      health: "/health",
      process: "/predict",
      processImage: "/imagexai-image",
    },
    steps: ["process"],
    requiresPolling: false, // TB model returns result immediately

    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    buildRequestBody: (step, params) => {
      if (step === "process") {
        return {
          path: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
          adminName1: params.adminName ? params.adminName.trim() : "",
          patientId: params.patientId ? params.patientId.trim() : "",
        };
      }
      return {};
    },
  },

  CXR_MULTICLASS: {
    id: "CXR_MULTICLASS",
    displayName: "CXR-Multiclass",
    baseEndpoint: `${BASE_URL}/CXR-Multiclass-Maira2`, // Remote API endpoint
    costKey: "CXR-Multiclass",
    supportedModalities: ["xray", "x-ray", "xr", "x ray"],
    endpoints: {
      health: "/health",
      generateReport: "/generate-report", // Updated to generate-report endpoint with grounding
    },
    steps: ["generateReport"], // Updated step name
    requiresPolling: false,
    useUrlEncoded: false, // Use multipart/form-data for image upload

    /**
     * Base parameters setup for CXR-Multiclass model
     * All parameters are already decrypted by AIModelHandler before being passed here
     * @param {string} patientId - Decrypted patient ID
     * @param {string} adminName - Decrypted admin FullName  
     * @param {string} decryptedFolderPath - Decrypted folder path for file access
     */
    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    buildRequestBody: (step, params) => {
      if (step === "generateReport") {
        return {
          // Note: image file will be added as FormData in AIModelHandler
          patientId: params.patientId ? params.patientId.trim() : "",
          adminName: params.adminName ? params.adminName.trim() : "",
          decryptedFolderPath: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
        };
      }
      return {};
    },
  },

  CXR_TB2: {
    id: "CXR_TB2",
    displayName: "CXR-TB2",
    baseEndpoint: `${BASE_URL}/NewTB`,
    costKey: "CXR-TB2",
    supportedModalities: ["xray", "x-ray", "xr", "x ray"],
    endpoints: {
      health: "/health",
      predict: "/predict",
    },
    steps: ["predict"],
    requiresPolling: false,
    useUrlEncoded: false, // Use multipart/form-data for image upload

    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
    }),

    buildRequestBody: (step, params) => {
      if (step === "predict") {
        return {
          // Note: file will be added as FormData in AIModelHandler
          patientId: params.patientId ? params.patientId.trim() : "",
          adminName: params.adminName ? params.adminName.trim() : "",
          decryptedFolderPath: params.decryptedFolderPath ? params.decryptedFolderPath.trim() : "",
        };
      }
      return {};
    },
  },

  // === LiverSegmentation Model ===
  LIVER_SEGMENTATION: {
    id: "LIVER_SEGMENTATION",
    displayName: "Liver-Segmentation",
    costKey: "Liver-Segmentation",
    supportedModalities: ["ct"],
    baseEndpoint: "https://api.health.easiofy.com/LiverSegmentation",
    endpoints: {
      health: "/health",
      dicomzip: "/dicomzip",
      segment: "/segment",
      merge: "/merge",
      glb: "/glb",
    },
    steps: ["dicomzip", "segment", "merge", "glb"],
    requiresPolling: false,
    useUrlEncoded: false, // Changed to false for multipart/form-data support
    processParams: (patientId, adminName, decryptedFolderPath) => ({
      patientId,
      adminName,
      decryptedFolderPath,
      model_name: "LiverSegmentation",
    }),
    buildRequestBody: (step, params) => {
      // Generate unique task_id with 4-digit random suffix
      const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
      const uniqueTaskId = params.uniqueTaskId || `${params.adminName}_${params.patientId}_${randomSuffix}`;

      // Store unique task_id for reuse in subsequent steps
      if (!params.uniqueTaskId) {
        params.uniqueTaskId = uniqueTaskId;
      }

      switch (step) {
        case "dicomzip":
          // For dicomzip step, we'll send a ZIP file via FormData in AIModelHandler
          return {
            zip_file: params.dicomZip, // Will be added as FormData in AIModelHandler
            task_id: uniqueTaskId,
          };
        case "segment":
          // For segment step, we'll send the dicom_path from dicomzip response
          return {
            dicom_path: params.dicom_path,
            task_id: params.uniqueTaskId || uniqueTaskId,
          };
        case "merge":
          return {
            liver_segments: params.output_files?.liver_segments || params.liver_segments,
            liver_vessels: params.output_files?.liver_vessels || params.liver_vessels,
            task_id: params.uniqueTaskId || uniqueTaskId,
          };
        case "glb":
          return {
            merged_nifti: params.merged_nifti,
            task_id: params.uniqueTaskId || uniqueTaskId,
          };
        default:
          return {};
      }
    },
  },
};